"use client"

export default function PricingCard({
  title,
  price = {},
  billingType = "monthly",
  limit,
  monthlyUrls,
  trafficLimit,
  highlight,
  isCurrent,
  button,
}) {
  const BRAND = {
    primary: "#3B82F6",
    primaryDark: "#2563EB",
    primarySoft: "#EFF6FF",
    text: "#1F2937",
    muted: "#4B5563",
    border: "#DBEAFE",
    success: "#22C55E",
    warning: "#F59E0B",
  }

  return (
    <div
      className={`card h-100 position-relative overflow-hidden`}
      style={{
        border: highlight ? `2px solid ${BRAND.primary}` : `1px solid ${BRAND.border}`,
        borderRadius: "16px",
        transition: "all 0.3s ease",
        background: highlight
          ? `linear-gradient(135deg, ${BRAND.primarySoft} 0%, #ffffff 100%)`
          : "#ffffff",
      }}
    >
      {/* Highlight badge */}
      {highlight && (
        <div
          className="position-absolute top-0 end-0 px-3 py-1 text-white fw-semibold"
          style={{
            background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)`,
            borderBottomLeftRadius: "12px",
            fontSize: "0.75rem",
            letterSpacing: "0.5px",
          }}
        >
          ⭐ POPULAR
        </div>
      )}

      <div className="card-body d-flex flex-column text-center p-4">
        {/* Plan title */}
        <h5
          className="card-title fw-bold mb-3"
          style={{ color: BRAND.text, fontSize: "1.5rem" }}
        >
          {title}
        </h5>

        {/* Price */}
        <div className="mb-4">
          <div className="d-flex align-items-baseline justify-content-center">
            <span
              className="fw-bold"
              style={{
                fontSize: "3rem",
                background: highlight
                  ? `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)`
                  : BRAND.text,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              ${billingType === "monthly" ? price.monthly : price.yearly}
            </span>
            <span className="ms-2 text-muted fs-6">
              /{billingType === "monthly" ? "mo" : "yr"}
            </span>
          </div>
        </div>

        {/* Divider */}
        <hr style={{ borderColor: BRAND.border, margin: "1.5rem 0" }} />

        {/* Key limits */}
        <ul className="list-unstyled text-start mb-4">
          {limit !== undefined && (
            <li className="mb-3 d-flex align-items-center">
              <span
                className="me-2"
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)`,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  color: "white",
                  flexShrink: 0,
                }}
              >
                🔗
              </span>
              <span style={{ color: BRAND.muted, fontSize: "0.95rem" }}>
                <strong style={{ color: BRAND.text }}>Active URLs:</strong>{" "}
                {limit === -1 ? "Unlimited" : limit.toLocaleString()}
              </span>
            </li>
          )}

          {monthlyUrls !== undefined && (
            <li className="mb-3 d-flex align-items-center">
              <span
                className="me-2"
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${BRAND.success} 0%, #16A34A 100%)`,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  color: "white",
                  flexShrink: 0,
                }}
              >
                📊
              </span>
              <span style={{ color: BRAND.muted, fontSize: "0.95rem" }}>
                <strong style={{ color: BRAND.text }}>URLs / Month:</strong>{" "}
                {monthlyUrls.toLocaleString()}
              </span>
            </li>
          )}

          {trafficLimit !== undefined && (
            <li className="mb-3 d-flex align-items-center">
              <span
                className="me-2"
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${BRAND.warning} 0%, #D97706 100%)`,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  color: "white",
                  flexShrink: 0,
                }}
              >
                👆
              </span>
              <span style={{ color: BRAND.muted, fontSize: "0.95rem" }}>
                <strong style={{ color: BRAND.text }}>Clicks / Month:</strong>{" "}
                {trafficLimit === -1 ? "Unlimited" : trafficLimit.toLocaleString()}
              </span>
            </li>
          )}
        </ul>

        {/* CTA Button */}
        <button
          onClick={button}
          disabled={isCurrent}
          className="mt-auto btn w-100 fw-semibold"
          style={{
            padding: "0.75rem 1.5rem",
            borderRadius: "10px",
            fontSize: "1rem",
            border: "none",
            background: isCurrent
              ? "#9ca3af"
              : highlight
                ? `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)`
                : BRAND.primarySoft,
            color: isCurrent || highlight ? "#ffffff" : BRAND.text,
            cursor: isCurrent ? "not-allowed" : "pointer",
            transition: "all 0.3s ease",
            boxShadow: highlight && !isCurrent ? `0 4px 12px rgba(59, 130, 246, 0.3)` : "none",
          }}
          onMouseEnter={(e) => {
            if (!isCurrent) {
              e.currentTarget.style.transform = "translateY(-2px)"
              e.currentTarget.style.boxShadow = highlight
                ? "0 8px 20px rgba(59, 130, 246, 0.4)"
                : "0 4px 12px rgba(0, 0, 0, 0.1)"
            }
          }}
          onMouseLeave={(e) => {
            if (!isCurrent) {
              e.currentTarget.style.transform = "translateY(0)"
              e.currentTarget.style.boxShadow = highlight
                ? "0 4px 12px rgba(59, 130, 246, 0.3)"
                : "none"
            }
          }}
        >
          {isCurrent ? "✓ Current Plan" : highlight ? "Get Started" : "Choose Plan"}
        </button>
      </div>
    </div>
  )
}
